import { useTranslation } from "react-i18next";
import { useInboxScheduledMessages } from "../../hooks";
import { useCallback } from "react";
import { formatDateTime, formatEmailTitle, formatName, formatPhone } from "@tellescope/utils";
import { cn } from "@tellescope/ui";
import { PaginatedList, Spinner } from "@tellescope/design-system";
import { InboxThreadListScheduledItem } from "./list-items/InboxThreadListScheduledItem";

export const InboxScheduledList = () => {
  const { t } = useTranslation("inbox");
  const { messages, isLoadingMessages, endusers, chatrooms, isError } = useInboxScheduledMessages();

  const formatTimestamp = useCallback(
    (date: Date) => {
      const timestamp = formatDateTime(date, "MMM D, YYYY at hh:mm a", {
        unit: "weeks",
        amount: 1,
        direction: "both",
      });

      if (date.getTime() < new Date().getTime()) {
        return t("{{timestamp}} (not sent)", { timestamp });
      }
      return timestamp;
    },
    [t]
  );

  const getItemProps = useCallback(
    (message: (typeof messages)[number]) => {
      switch (message.channel) {
        case "Chat":
          return {
            title: chatrooms?.chatroomsMap[message.roomId]?.title ?? t("Unknown chat"),
            timestamp: message.sendAt ? formatTimestamp(message.sendAt) : t("Unknown time"),
            preview: message.message,
            channel: message.channel,
          };
        case "SMS": {
          const enduser = endusers?.endusersMap[message.enduserId];
          const title = enduser
            ? formatName(enduser)
            : message.enduserPhoneNumber
            ? formatPhone(message.enduserPhoneNumber)
            : t("Unknown number");

          return {
            title,
            timestamp: message.sendAt ? formatTimestamp(message.sendAt) : t("Unknown time"),
            preview: message.message,
            channel: message.channel,
          };
        }
        case "Email":
          return {
            title: formatEmailTitle(message.subject),
            timestamp: message.sendAt ? formatTimestamp(message.sendAt) : t("Unknown time"),
            preview: message.textContent,
            channel: message.channel,
          };
      }
    },
    [chatrooms?.chatroomsMap, endusers?.endusersMap, formatTimestamp, t]
  );

  if (isError) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <div className="text-red-500 mb-2">{t("Error loading inbox threads")}</div>
      </div>
    );
  }

  if (isLoadingMessages) {
    return (
      <div className="w-full h-full flex items-center justify-center text-center text-muted-foreground">
        <Spinner />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex p-4 h-full text-center text-muted-foreground items-center justify-center">
        {t("No scheduled messages found")}
      </div>
    );
  }

  return (
    <PaginatedList className="p-4 space-y-2 flex-1">
      {messages.map((message) => {
        const isSending = message.sendAt && message.sendAt.getTime() < new Date().getTime();
        const { timestamp, ...props } = getItemProps(message);

        return (
          <InboxThreadListScheduledItem
            key={message.id}
            message={message}
            {...props}
            timestamp={isSending ? <SendingStatus /> : timestamp}
            className={cn("my-1.5 max-w-[750px] mx-auto", isSending && "opacity-70")}
            showActions={isSending ? false : undefined}
            enduserIds={message.enduserId ? [message.enduserId] : []}
            chatroomsMap={chatrooms?.chatroomsMap ?? {}}
          />
        );
      })}
    </PaginatedList>
  );
};

const SendingStatus = () => {
  const { t } = useTranslation("inbox");

  return (
    <div className="flex gap-1 items-center">
      <Spinner size="xxs" color="secondary" />
      {t("Sending...")}
    </div>
  );
};
